import { z } from 'zod';

import { ContentType, UserGender, MembershipResponseType } from '../../enums';

export const CommonToolNames = {
  RagResult: 'rag_result',
  RagResultOperator: 'rag_result_operator',
  UsageLimitsError: 'usageLimitsError',
  Membership: 'membership',
  Authentication: 'authentication',
  MembershipStatus: 'membership_status',
  MembershipPause: 'membership_pause',
  UserProfile: 'user_profile',
  GroupClass: 'group_class',
} as const;

export const ragResultToolDef = z.object({
  content: z
    .string()
    .describe(
      'Main response with factual information from documents, formatted in markdown',
    ),
  sources: z
    .array(
      z.object({
        type: z.nativeEnum(ContentType).describe('Source document type'),
        fileName: z
          .string()
          .describe(
            'Prettified source document file name from metadata. Remove underscores and capitalize. Drop file extensions.',
          )
          .nullable(),
        url: z.string().describe('Document URL from metadata').nullable(),
        page: z
          .string()
          .describe(
            'Number of page in the document on which the information was found. The page number is stored in the metadata. Take the number of page as it is and DO NOT modify it.',
          )
          .nullable(),
        header: z
          .string()
          .describe(
            'Legal Journal Reference Number in the exact format "č. {number}/{year} LJ" (e.g. "č. 4/2023 LJ"). Extract only if it matches this exact format.',
          )
          .nullable(),
        article: z
          .string()
          .describe(
            'Article (článek) reference in legal documents in the exact format "čl. {number}" (e.g. "čl. 5"). This represents a specific section of the legal document. Extract only if it matches this exact format.',
          )
          .nullable(),
      }),
    )
    .nullable()
    .describe('List of source documents referenced in the response'),
  answered: z.boolean().describe('Whether the question was answered'),
});

export const ragResultToolOperator = z.object({
  content: z
    .string()
    .describe(
      'natural conversationally response to the user, use the chat history to form the response',
    ),
  operator: z
    .object({
      name: z.string().describe("Selected operator's name"),
      email: z.string().describe("Selected operator's email"),
      phone: z.string().describe("Selected operator's phone"),
      displayText: z.string().describe("Selected operator's displayText"),
    })
    .nullable()
    .describe('Operator contact information'),
});

export type RagResultToolDef = z.infer<typeof ragResultToolDef>;
export type RagResultToolOperator = z.infer<typeof ragResultToolOperator>;

/**
 * Manual "tool", we use this to provide better error messages to the
 * user when limits are exceeded. This tool is not really a LLM tool,
 * but we insert it manually on client side, leveraging the tool registry.
 */
export const usageLimitsErrorToolArgsSchema = z.object({
  operator: z
    .object({
      name: z.string().describe('Operator name').nullable(),
      email: z.string().describe('Operator email').nullable(),
      phone: z.string().describe('Operator phone').nullable(),
    })
    .nullable(),
});

export type UsageLimitsErrorToolArgs = z.infer<
  typeof usageLimitsErrorToolArgsSchema
>;

export const authenticationToolDef = z
  .object({
    content: z
      .string()
      .describe(
        'Natural conversational response. Format using markdown when appropriate.',
      ),
    shouldLogin: z.boolean().describe('Whether the user should login'),
    shouldRegister: z
      .boolean()
      .describe('Whether the user should be prompted to register'),
  })
  .describe('Membership prompt for logging in or registration');

export type AuthenticationToolDef = z.infer<typeof authenticationToolDef>;

export const userProfileToolDef = z
  .object({
    content: z
      .string()
      .describe(
        'Natural conversational response about user profile. Format using markdown when appropriate.',
      ),
    firstName: z.string().describe('User first name'),
    lastName: z.string().describe('User last name'),
    email: z.string().describe('User email'),
    phone: z.string().optional().describe('User phone number'),
    gender: z.nativeEnum(UserGender).optional().describe('User gender'),
    birthDate: z.string().optional().describe('User birth date'),
    address: z.string().optional().describe('User address'),
    isEmailVerified: z.boolean().describe('Whether the email is verified'),
  })
  .describe('User profile information parsed from member_data');

export type UserProfileToolDef = z.infer<typeof userProfileToolDef>;

export const membershipPauseToolDef = z.object({
  content: z
    .string()
    .describe(
      'Natural conversational response about user profile. Format using markdown when appropriate.',
    ),
  showPauseMembershipButton: z
    .boolean()
    .describe(
      'Whether to show pause membership button. Show when: 1) User explicitly asks to pause an ongoing membership.',
    ),
});

export type MembershipPauseToolDef = z.infer<typeof membershipPauseToolDef>;

export const membershipStatusToolDef = z
  .object({
    content: z
      .string()
      .describe(
        'Natural conversational response. Format using markdown when appropriate.',
      ),
    activeMembership: z
      .string()
      .optional()
      .describe(
        'Name of the active membership plan. Should be exactly as stored in the system.',
      ),
    activeFrom: z
      .string()
      .describe('Membership start date in ISO format (YYYY-MM-DD).')
      .optional(),
    activeTo: z
      .string()
      .describe('Membership end date in ISO format (YYYY-MM-DD).')
      .optional(),
    isExpired: z.boolean().describe('Whether the membership is expired'),
    showNewMembershipButton: z
      .boolean()
      .describe(
        'Whether to show new membership button. Show when: 1) User explicitly asks about new membership, 2) When membership_data is missing, 3) User asks about extending membership.',
      ),
    // TODO: This is here to disable the pauseMembership functionality
    showPauseMembershipButton: z
      .boolean()
      .describe(
        'Whether to show pause membership button. Show when: 1) User explicitly asks to pause an ongoing membership.',
      ),
  })
  .describe(
    'Current membership status, when type is membership_status, even when membership data are empty',
  );

export type MembershipStatusToolDef = z.infer<typeof membershipStatusToolDef>;

/**
 * Tool definition for eFitness membership related questions.
 * We define multiple output structures, which we use
 * based on the LLM response type.
 */
export const membershipToolDef = z.object({
  type: z
    .nativeEnum(MembershipResponseType)
    .describe('Type of response based on user query intent'),
  content: z
    .string()
    .describe(
      'Natural conversational response. Format using markdown when appropriate.',
    ),
  error: z
    .string()
    .optional()
    .describe('Error message if tool execution failed.'),
  // Tool for showing current membership status
  membership_status: z
    .object({
      activeMembership: z
        .string()
        .optional()
        .describe(
          'Name of the active membership plan. Should be exactly as stored in the system.',
        ),
      activeFrom: z
        .string()
        .describe('Membership start date in ISO format (YYYY-MM-DD).')
        .optional(),
      activeTo: z
        .string()
        .describe('Membership end date in ISO format (YYYY-MM-DD).')
        .optional(),
      isExpired: z.boolean().describe('Whether the membership is expired'),
      showNewMembershipButton: z
        .boolean()
        .describe(
          'Whether to show new membership button. Show when: 1) User explicitly asks about new membership, 2) Current membership is expired, 3) User asks about extending membership.',
        ),
      showPauseMembershipButton: z
        .boolean()
        .describe(
          'Whether to show pause membership button. Show when: 1) User explicitly asks to pause an ongoing membership.',
        ),
    })
    .optional()
    .describe(
      'Current membership status, when type is membership_status, even when membership data are empty',
    ),
  // Tool for showing user profile information
  user_profile: z
    .object({
      firstName: z.string().describe('User first name'),
      lastName: z.string().describe('User last name'),
      email: z.string().describe('User email'),
      phone: z.string().optional().describe('User phone number'),
      gender: z.nativeEnum(UserGender).optional().describe('User gender'),
      birthDate: z.string().optional().describe('User birth date'),
      address: z.string().optional().describe('User address'),
      isEmailVerified: z.boolean().describe('Whether the email is verified'),
    })
    .optional()
    .describe('User profile information, when type is user_profile'),

  // Tool for membership registration prompt
  membership_registration: z
    .object({
      shouldRegister: z
        .boolean()
        .describe('Whether the user should be prompted to register'),
    })
    .optional()
    .describe('Registration prompt, when type is membership_registration'),
  // Tool for login prompt
  membership_login: z
    .object({
      shouldLogin: z.boolean().describe('Whether the user should login'),
    })
    .optional()
    .describe('Login prompt, when type is membership_login'),

  // Tool for other topics
  other: z
    .object({
      type: z.literal(MembershipResponseType.Other),
    })
    .optional(),
});

export type MembershipToolDef = z.infer<typeof membershipToolDef>;

/**
 * Tool definition for eFitness group class related questions.
 */
export const groupClassToolDef = z.object({
  content: z
    .string()
    .describe('Natural conversational response about group classes.'),
  error: z
    .string()
    .optional()
    .describe('Error message if tool execution failed.'),
});

export type GroupClassToolDef = z.infer<typeof groupClassToolDef>;
